// Premium checkout. Only reachable when REACT_APP_PREMIUM_ENABLED=true.
//
// Flow: (1) ask OUR server to create an order (it picks the price and knows who
// you are from your sign-in token), (2) pay in Razorpay's window, (3) ask OUR
// server to confirm the payment with Razorpay. Only the server can grant Premium;
// this component never writes Premium to the database itself.
import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { PLAN } from "../config/plan";
import { Crown, Check, X, Loader } from "lucide-react";

const FEATURES = [
  "6 recommendations per search",
  "Unlimited searches per day",
  "No ads",
];

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const RazorpayCheckout = ({ onSuccess, onCancel }) => {
  const { user, reloadUserData } = useAuth();
  const [status, setStatus] = useState("idle"); // idle | working | success
  const [error, setError] = useState("");
  const [until, setUntil] = useState("");

  const confirmWithServer = async (response, token) => {
    const res = await fetch("/api/verify-razorpay-payment", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.success) {
      throw new Error(
        data.error ||
          "We could not confirm your payment yet. If money was taken, Premium will be activated automatically within a few minutes.",
      );
    }
    return data;
  };

  const handlePay = async () => {
    setError("");
    setStatus("working");
    try {
      if (!(await loadRazorpayScript())) {
        throw new Error(
          "Could not load the payment window. Check your connection and try again.",
        );
      }

      const token = await user.getIdToken();
      const orderRes = await fetch("/api/create-razorpay-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: "{}", // nothing to send: the server decides price and plan
      });
      const order = await orderRes.json().catch(() => ({}));
      if (!orderRes.ok) {
        throw new Error(order.error || "Could not start the payment.");
      }

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: "NORYQ",
        description: order.planName,
        order_id: order.orderId,
        prefill: { email: user.email || "" },
        theme: { color: "#667eea" },
        handler: async (response) => {
          try {
            const fresh = await user.getIdToken(true);
            const data = await confirmWithServer(response, fresh);
            await reloadUserData();
            setUntil(data.premiumUntil);
            setStatus("success");
          } catch (e) {
            setError(e.message);
            setStatus("idle");
          }
        },
        modal: {
          ondismiss: () => setStatus((s) => (s === "working" ? "idle" : s)),
        },
      });
      rzp.on("payment.failed", (r) => {
        setError(
          r?.error?.description ||
            "The payment failed. You have not been charged.",
        );
        setStatus("idle");
      });
      rzp.open();
    } catch (e) {
      setError(e.message);
      setStatus("idle");
    }
  };

  const link = { color: "#fff", textDecoration: "underline" };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Buy NORYQ Premium"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
        padding: "1rem",
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: 24,
          padding: "2rem",
          maxWidth: 480,
          width: "100%",
          maxHeight: "92vh",
          overflowY: "auto",
          position: "relative",
          color: "#fff",
        }}
      >
        {status !== "working" && (
          <button
            type="button"
            onClick={onCancel}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "50%",
              width: 34,
              height: 34,
              cursor: "pointer",
              color: "#fff",
            }}
          >
            <X size={18} />
          </button>
        )}

        <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
          <Crown size={40} />
          <h2
            style={{
              margin: "0.5rem 0 0.25rem",
              fontSize: "1.6rem",
              fontWeight: 800,
            }}
          >
            NORYQ Premium
          </h2>
          <div style={{ fontSize: "2.2rem", fontWeight: 800 }}>
            ₹{PLAN.priceInr}
            <span style={{ fontSize: "1rem", fontWeight: 400 }}>
              {" "}
              / {PLAN.durationDays} days
            </span>
          </div>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.9rem", opacity: 0.95 }}>
            One-time payment. It does <strong>not</strong> renew automatically.
          </p>
        </div>

        {status === "success" ? (
          <div style={{ textAlign: "center" }}>
            <h3 style={{ fontSize: "1.2rem" }}>You are Premium</h3>
            <p style={{ opacity: 0.95 }}>
              Active until {formatDate(until)}. Razorpay will email you a
              payment receipt.
            </p>
            <button
              type="button"
              onClick={onSuccess}
              style={{
                marginTop: "0.5rem",
                padding: "0.8rem 2rem",
                borderRadius: 12,
                border: "none",
                background: "#fff",
                color: "#667eea",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <ul style={{ listStyle: "none", padding: 0, margin: "0 0 1.25rem" }}>
              {FEATURES.map((f) => (
                <li
                  key={f}
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    alignItems: "center",
                    padding: "0.3rem 0",
                  }}
                >
                  <Check size={18} /> {f}
                </li>
              ))}
            </ul>

            {error && (
              <div
                role="alert"
                style={{
                  background: "rgba(255,255,255,0.18)",
                  borderRadius: 10,
                  padding: "0.75rem",
                  marginBottom: "1rem",
                  fontSize: "0.88rem",
                }}
              >
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handlePay}
              disabled={status === "working"}
              style={{
                width: "100%",
                padding: "1rem",
                borderRadius: 12,
                border: "none",
                background: "#fff",
                color: "#667eea",
                fontWeight: 800,
                fontSize: "1.05rem",
                cursor: status === "working" ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
              }}
            >
              {status === "working" ? (
                <>
                  <Loader size={18} /> Please wait...
                </>
              ) : (
                <>Pay ₹{PLAN.priceInr} with Razorpay</>
              )}
            </button>

            <p
              style={{
                fontSize: "0.78rem",
                lineHeight: 1.6,
                opacity: 0.92,
                margin: "1rem 0 0",
                textAlign: "center",
              }}
            >
              The price shown is the full price. By paying you agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={link}>
                Terms
              </a>{" "}
              and{" "}
              <a href="/refund" target="_blank" rel="noopener noreferrer" style={link}>
                Refund &amp; Cancellation Policy
              </a>
              . Payment is handled by Razorpay. NORYQ never sees your card, UPI
              or bank details.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default RazorpayCheckout;
